// src/components/InvestorList.js
import React from "react";

const InvestorList = ({ investors }) => {
  // Graceful fallback guard clause matching empty search filters safely
  if (!investors || investors.length === 0) {
    return (
      <div className="py-12 border-2 border-dashed border-gray-200 rounded-2xl bg-gray-50/50">
        <p className="text-center font-medium text-gray-500">
          No active investors found matching this criteria.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Optimized responsive grid containing zero layout thrashing factors */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
        {investors.map((investor) => {
          // Absolute fallback routing protection
          const detailUrl = investor.profileSlug || "/investors";

          return (
            <div
              key={investor.id}
              className="group border border-gray-100 rounded-2xl p-5 bg-white shadow-sm hover:shadow-xl hover:border-blue-100 hover:-translate-y-1 transition-all duration-300 relative flex flex-col justify-between overflow-hidden"
            >
              {/* Subtle background glow effect using pure hardware-accelerated Tailwind classes */}
              <div className="absolute -inset-px bg-gradient-to-r from-blue-50 to-indigo-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none -z-10" />

              <div>
                {/* Profile Image Shell with lazy-load optimizations built in */}
                <div className="w-full flex items-center justify-center mb-4">
                  <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-50 border border-gray-100 shadow-inner group-hover:scale-105 transition-transform duration-300">
                    <img
                      src={investor.photoURL || "/default-avatar.png"}
                      alt={`Startup Investor ${investor.name}`}
                      loading="lazy"
                      className="w-full h-full object-cover object-center block select-none"
                    />
                  </div>
                </div>

                {/* Localized Category Badging UI */}
                <div className="flex justify-center mb-2">
                  <span className="text-xs font-bold px-2.5 py-1 bg-blue-50 text-blue-600 rounded-full uppercase tracking-wider">
                    {investor.type || "Angel Network"}
                  </span>
                </div>

                {/* Text Metadata Tranches */}
                <h3 className="font-bold text-xl text-center text-gray-900 group-hover:text-blue-600 transition-colors duration-200">
                  {investor.name}
                </h3>
                <p className="text-sm font-semibold text-gray-500 text-center flex items-center justify-center gap-1 mt-0.5">
                  📍 {investor.location}
                </p>
                <p className="text-xs font-medium text-gray-400 text-center mt-2 px-3 py-1 bg-gray-50 rounded-lg inline-block mx-auto w-fit">
                  🎯 Focus: {investor.industry}
                </p>
              </div>

              {/* Action Layer Component Block */}
              <div className="mt-5 pt-4 border-t border-gray-50 flex flex-col gap-3">
                <div className="flex items-center justify-between text-xs font-medium text-gray-400">
                  <span>Last Seen Activity:</span>
                  <span className="text-green-500 font-semibold flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                    {investor.lastSeen || "Active 2026"}
                  </span>
                </div>

                <a
                  href={detailUrl}
                  className="w-full text-center block text-sm font-bold py-2.5 px-4 rounded-xl bg-gray-900 text-white hover:bg-blue-600 shadow-sm hover:shadow-md hover:shadow-blue-500/10 transition-all duration-200"
                >
                  View Allocation Profile →
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InvestorList;
